/**
 * Quién tiene cambios sin guardar, para que salirse no los tire.
 *
 * El panel navega sin recargar la página, así que irse de una pantalla a medio
 * escribir es un clic en el menú: no hay aviso del navegador que lo detenga.
 * Cada pantalla que edita algo se apunta aquí mientras esté sucia, y el que
 * navega —el menú, las migas, el botón de volver, el atrás del navegador—
 * pregunta antes de moverse.
 *
 * Lo que ocurre al bloquear no es un diálogo: es hacer parpadear la barra de
 * Guardar y descartar que ya está arriba a la derecha. La respuesta a «¿por qué
 * no me deja salir?» está en el mismo sitio que la solución.
 *
 * Antes cada pantalla se cableaba a mano hasta arriba —dos banderas, dos
 * contadores y la misma comprobación repetida en siete sitios—, y la que se
 * añadía nueva se olvidaba de hacerlo.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

/** Una pantalla con cambios sin guardar y cómo pedirle que se haga notar. */
type Blocker = { dirty: boolean; flash: () => void }

type Registry = {
  /** Apunta una pantalla; devuelve cómo borrarla al desmontarse. */
  add: (blocker: Blocker) => () => void
  /** Aviso de que alguna cambió de estado. */
  changed: () => void
  /** `true` si hay algo sin guardar. Además hace parpadear su barra. */
  block: () => boolean
}

const UnsavedChangesContext = createContext<Registry | null>(null)

/**
 * El registro, para quien lo hospeda (la página que también navega).
 *
 * Devuelve el valor que hay que pasar al proveedor, la función que usan sus
 * propios enlaces, y si hay algo pendiente —que hace falta para poner o quitar
 * el vigilante del botón atrás del navegador—.
 */
export function useUnsavedRegistry() {
  const blockers = useRef(new Set<Blocker>())
  const [dirtyCount, setDirtyCount] = useState(0)

  const recount = useCallback(() => {
    let pending = 0
    for (const blocker of blockers.current) if (blocker.dirty) pending += 1
    setDirtyCount(pending)
  }, [])

  const block = useCallback(() => {
    for (const blocker of blockers.current) {
      if (!blocker.dirty) continue
      blocker.flash()
      return true
    }
    return false
  }, [])

  /* El aviso del navegador al cerrar la pestaña o recargar. Uno para todo el
     panel: antes lo ponía cada pantalla por su cuenta, así que lo tenían tres
     de las nueve que editan algo. */
  useEffect(() => {
    if (dirtyCount === 0) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirtyCount])

  const registry = useMemo<Registry>(() => ({
    add: (blocker) => {
      blockers.current.add(blocker)
      recount()
      return () => { blockers.current.delete(blocker); recount() }
    },
    changed: recount,
    block,
  }), [block, recount])

  return { registry, block, hasUnsaved: dirtyCount > 0, UnsavedChangesProvider: UnsavedChangesContext.Provider }
}

/**
 * Apunta esta pantalla mientras tenga cambios sin guardar.
 *
 * `flash` es lo que se le pide cuando alguien intenta irse. Se guarda en una
 * ref y no en el registro para que siempre sea la última: la función se vuelve
 * a crear en cada render y una copia vieja avisaría a una barra que ya no está.
 */
export function useUnsavedChanges(dirty: boolean, flash: () => void) {
  const registry = useContext(UnsavedChangesContext)
  const blocker = useRef<Blocker>({ dirty, flash })
  blocker.current.flash = flash

  useEffect(() => {
    if (!registry) return
    return registry.add(blocker.current)
  }, [registry])

  useEffect(() => {
    blocker.current.dirty = dirty
    registry?.changed()
  }, [dirty, registry])
}

/**
 * Para los enlaces que se van de la pantalla: `if (guard()) return`.
 *
 * Sin proveedor devuelve siempre `false` —nada que bloquear— para que un
 * componente suelto, o una prueba, no tengan que montar nada.
 */
export function useUnsavedGuard(): () => boolean {
  const registry = useContext(UnsavedChangesContext)
  return useCallback(() => registry?.block() ?? false, [registry])
}
